import React, { createContext, useContext, useState, useMemo, useEffect } from 'react'
import { arrayUnion } from 'firebase/firestore'
import { getInitials } from '../data/mockData'
import { useAuth } from './AuthContext'
import * as firestoreService from '../services/firestore'

const AppContext = createContext(null)

export function AppProvider({ children }) {
  const { user: firebaseUser } = useAuth()
  const [events, setEvents] = useState([])
  const [tasks, setTasks] = useState([])
  const [guests, setGuests] = useState({})
  const [budget, setBudget] = useState({})
  const [shopping, setShopping] = useState({})
  const [meals, setMeals] = useState({})
  const [inventar, setInventar] = useState({})
  const [catering, setCatering] = useState({})
  const [restaurants, setRestaurants] = useState({})
  const [musik, setMusik] = useState({})
  const [transport, setTransport] = useState({})
  const [venues, setVenues] = useState({})
  const [files, setFiles] = useState({})
  const [invitations, setInvitations] = useState([])
  const [handledInvitations, setHandledInvitations] = useState(new Set())
  const [toastMessage, setToastMessage] = useState(null)

  const showToast = (message) => {
    setToastMessage(message)
  }

  const currentUser = useMemo(() => ({
    id: firebaseUser?.uid || 'user-1',
    name: firebaseUser?.displayName || 'Bruger',
    email: firebaseUser?.email || '',
  }), [firebaseUser])

  // Subscribe to Firestore events
  useEffect(() => {
    if (!firebaseUser?.uid) return

    const unsubscribe = firestoreService.subscribeToUserEvents(firebaseUser.uid, (firestoreEvents) => {
      setEvents(firestoreEvents)
    })

    return () => unsubscribe()
  }, [firebaseUser?.uid])

  // Subscribe to invitations
  useEffect(() => {
    const email = firebaseUser?.email || currentUser.email
    if (!email) return

    const unsubscribe = firestoreService.subscribeToUserInvitations(email, (allInvitations) => {
      // Filter out invitations that have been locally handled
      const filtered = allInvitations.filter(inv => !handledInvitations.has(inv.id))
      setInvitations(filtered)
    })

    return () => unsubscribe()
  }, [firebaseUser?.email, currentUser.email, handledInvitations])

  // Subscribe to meals for all events
  useEffect(() => {
    if (!events || events.length === 0) return

    const unsubscribers = events.map(event =>
      firestoreService.subscribeToMeals(event.id, (mealsList) => {
        setMeals(prev => ({
          ...prev,
          [event.id]: mealsList
        }))
      })
    )

    return () => unsubscribers.forEach(unsub => unsub?.())
  }, [events])

  // Subscribe to tasks for all events
  useEffect(() => {
    if (!events || events.length === 0) return

    const unsubscribers = events.map(event =>
      firestoreService.subscribeToTasks(event.id, (tasksList) => {
        setTasks(prev => [...prev.filter(t => t.eventId !== event.id), ...tasksList.map(t => ({ ...t, eventId: event.id }))])
      })
    )

    return () => unsubscribers.forEach(unsub => unsub?.())
  }, [events])

  // Subscribe to guests for all events
  useEffect(() => {
    if (!events || events.length === 0) return

    const unsubscribers = events.map(event =>
      firestoreService.subscribeToGuests(event.id, (guestsList) => {
        setGuests(prev => ({
          ...prev,
          [event.id]: guestsList
        }))
      })
    )

    return () => unsubscribers.forEach(unsub => unsub?.())
  }, [events])

  // Subscribe to shopping for all events
  useEffect(() => {
    if (!events || events.length === 0) return

    const unsubscribers = events.map(event =>
      firestoreService.subscribeToShopping(event.id, (shoppingList) => {
        setShopping(prev => ({
          ...prev,
          [event.id]: shoppingList
        }))
      })
    )

    return () => unsubscribers.forEach(unsub => unsub?.())
  }, [events])

  // Subscribe to budget for all events
  useEffect(() => {
    if (!events || events.length === 0) return

    const unsubscribers = events.map(event =>
      firestoreService.subscribeToBudget(event.id, (budgetList) => {
        // Transform flat budget items into categories with items
        const grouped = budgetList.reduce((acc, item) => {
          const catId = item.categoryId || item.categoryName
          if (!acc[catId]) {
            acc[catId] = {
              id: catId,
              name: item.categoryName,
              color: item.color,
              bgColor: item.bgColor,
              budget: 0,
              items: []
            }
          }
          acc[catId].items.push({
            id: item.id,
            name: item.itemName,
            amount: item.amount
          })
          return acc
        }, {})

        setBudget(prev => ({
          ...prev,
          [event.id]: Object.values(grouped)
        }))
      })
    )

    return () => unsubscribers.forEach(unsub => unsub?.())
  }, [events])

  // Subscribe to inventar for all events
  useEffect(() => {
    if (!events || events.length === 0) return

    const unsubscribers = events.map(event =>
      firestoreService.subscribeToInventar(event.id, (inventarList) => {
        setInventar(prev => ({
          ...prev,
          [event.id]: inventarList
        }))
      })
    )

    return () => unsubscribers.forEach(unsub => unsub?.())
  }, [events])

  // Subscribe to catering for all events
  useEffect(() => {
    if (!events || events.length === 0) return

    const unsubscribers = events.map(event =>
      firestoreService.subscribeToCatering(event.id, (cateringList) => {
        setCatering(prev => ({
          ...prev,
          [event.id]: cateringList
        }))
      })
    )

    return () => unsubscribers.forEach(unsub => unsub?.())
  }, [events])

  // Subscribe to restaurants for all events
  useEffect(() => {
    if (!events || events.length === 0) return

    const unsubscribers = events.map(event =>
      firestoreService.subscribeToRestaurants(event.id, (restaurantsList) => {
        setRestaurants(prev => ({
          ...prev,
          [event.id]: restaurantsList
        }))
      })
    )

    return () => unsubscribers.forEach(unsub => unsub?.())
  }, [events])

  // Subscribe to musik for all events
  useEffect(() => {
    if (!events || events.length === 0) return

    const unsubscribers = events.map(event =>
      firestoreService.subscribeToMusik(event.id, (musikList) => {
        setMusik(prev => ({
          ...prev,
          [event.id]: musikList
        }))
      })
    )

    return () => unsubscribers.forEach(unsub => unsub?.())
  }, [events])

  // Subscribe to transport for all events
  useEffect(() => {
    if (!events || events.length === 0) return

    const unsubscribers = events.map(event =>
      firestoreService.subscribeToTransport(event.id, (transportList) => {
        setTransport(prev => ({
          ...prev,
          [event.id]: transportList
        }))
      })
    )

    return () => unsubscribers.forEach(unsub => unsub?.())
  }, [events])

  // Subscribe to venues for all events
  useEffect(() => {
    if (!events || events.length === 0) return

    const unsubscribers = events.map(event =>
      firestoreService.subscribeToVenues(event.id, (venuesList) => {
        setVenues(prev => ({
          ...prev,
          [event.id]: venuesList
        }))
      })
    )

    return () => unsubscribers.forEach(unsub => unsub?.())
  }, [events])

  // Subscribe to files for all events
  useEffect(() => {
    if (!events || events.length === 0) return

    const unsubscribers = events.map(event =>
      firestoreService.subscribeToFiles(event.id, (filesList) => {
        setFiles(prev => ({
          ...prev,
          [event.id]: filesList
        }))
      })
    )

    return () => unsubscribers.forEach(unsub => unsub?.())
  }, [events])

  const toggleTask = async (taskId) => {
    const task = tasks.find(t => t.id === taskId)
    if (!task?.eventId) return
    try {
      await firestoreService.updateTaskFirestore(task.eventId, taskId, { done: !task.done })
    } catch (error) {
      console.error('Error toggling task:', error)
    }
  }

  const addTask = async (eventId, task) => {
    try {
      const taskData = { ...task, done: false, comments: [], createdAt: new Date().toISOString() }
      // Remove undefined values
      Object.keys(taskData).forEach(key => taskData[key] === undefined && delete taskData[key])
      await firestoreService.addTaskFirestore(eventId, taskData)
      showToast('Opgave oprettet! ✓')
    } catch (error) {
      console.error('Error adding task:', error)
      throw error
    }
  }

  const updateTask = async (taskId, updates) => {
    const task = tasks.find(t => t.id === taskId)
    if (!task?.eventId) return
    try {
      await firestoreService.updateTaskFirestore(task.eventId, taskId, updates)
    } catch (error) {
      console.error('Error updating task:', error)
      throw error
    }
  }

  const addComment = async (taskId, text, author) => {
    const task = tasks.find(t => t.id === taskId)
    if (!task?.eventId) return
    try {
      const newComments = [...(task.comments || []), { id: Date.now().toString(), author, text, time: 'nu' }]
      const updates = { comments: newComments }
      // Remove undefined values from updates
      Object.keys(updates).forEach(key => updates[key] === undefined && delete updates[key])
      // Update local state immediately
      setTasks(prev => prev.map(t => t.id === taskId ? { ...t, comments: newComments } : t))
      await firestoreService.updateTaskFirestore(task.eventId, taskId, updates)
    } catch (error) {
      console.error('Error adding comment:', error)
    }
  }

  const updateGuestStatus = async (eventId, guestId, status) => {
    try {
      await firestoreService.updateGuestFirestore(eventId, guestId, { status })
    } catch (error) {
      console.error('Error updating guest status:', error)
    }
  }

  const addGuest = async (eventId, guest) => {
    try {
      const guestData = { ...guest, status: 'pending' }
      // Remove undefined values
      Object.keys(guestData).forEach(key => guestData[key] === undefined && delete guestData[key])
      await firestoreService.addGuestFirestore(eventId, guestData)
    } catch (error) {
      console.error('Error adding guest:', error)
      throw error
    }
  }

  const shoppingCategories = [
    { name: 'Kolonial', color: '#8B7355', bgColor: '#f5f0e8' },
    { name: 'Frugt & Grønt', color: '#2d7a3e', bgColor: '#e8faf0' },
    { name: 'Mejeri produkter', color: '#c9a961', bgColor: '#fff5e6' },
    { name: 'Kød & Fisk', color: '#a85555', bgColor: '#ffebeb' },
    { name: 'Frost', color: '#4a90e2', bgColor: '#e8f4ff' },
    { name: 'Drikkevarer', color: '#279089', bgColor: '#e8f7f6' },
    { name: 'Slik & Snacks', color: '#d17a3e', bgColor: '#ffe8d6' },
  ]

  const addBudgetItem = async (eventId, categoryId, name, amount) => {
    const budgetCategories = [
      { name: 'Mad', color: '#923232', bgColor: '#ffe4e4' },
      { name: 'Drikkevarer', color: '#279089', bgColor: '#e8f7f6' },
      { name: 'Dekoration', color: '#b89a09', bgColor: '#fff9e6' },
      { name: 'Stole & Borde', color: '#559c8f', bgColor: '#e8f9f5' },
      { name: 'Invitationer', color: '#4b5b9c', bgColor: '#f3f2ff' },
      { name: 'Musik & Underholdning', color: '#a05e64', bgColor: '#ffefef' },
      { name: 'Transport', color: '#5b8978', bgColor: '#f0faf8' },
      { name: 'Blomster', color: '#744d3d', bgColor: '#ffe8d6' },
    ]

    try {
      const catData = budgetCategories.find(c => c.name === categoryId)
      const budgetData = {
        categoryId,
        categoryName: categoryId,
        color: catData?.color || '#6c63ff',
        bgColor: catData?.bgColor || '#f0eeff',
        itemName: name,
        amount: parseFloat(amount),
      }
      // Remove undefined values
      Object.keys(budgetData).forEach(key => budgetData[key] === undefined && delete budgetData[key])
      await firestoreService.addBudgetItemFirestore(eventId, budgetData)
    } catch (error) {
      console.error('Error adding budget item:', error)
      throw error
    }
  }

  const updateShoppingQty = async (eventId, itemId, qty) => {
    try {
      await firestoreService.updateShoppingItemFirestore(eventId, itemId, { quantity: Math.max(1, qty) })
    } catch (error) {
      console.error('Error updating shopping quantity:', error)
    }
  }

  const toggleShoppingPurchased = async (eventId, itemId) => {
    const item = shopping[eventId]?.find(i => i.id === itemId)
    console.log('Toggle purchased - item:', item)
    if (!item) return
    try {
      const newPurchasedStatus = !item.purchased
      await firestoreService.updateShoppingItemFirestore(eventId, itemId, { purchased: newPurchasedStatus })

      const category = item.category || 'Mad'
      const budgetCategory = budget[eventId]?.find(cat => cat.name === category)
      const budgetItem = budgetCategory?.items?.find(bItem => bItem.name === item.name)

      // Auto-add to budget when marked as purchased (only if not already in budget)
      if (newPurchasedStatus && item.price) {
        // Check if item already exists in budget for this category
        if (!budgetItem) {
          const totalAmount = item.price * (item.quantity || 1)
          console.log('Adding to budget:', { category, name: item.name, amount: totalAmount })
          await addBudgetItem(eventId, category, item.name, totalAmount)
          showToast('Tilføjet til budget! ✓')
        } else {
          console.log('Item already in budget, skipping duplicate')
          showToast('Varen er allerede på budgettet')
        }
      } else if (!newPurchasedStatus && budgetItem) {
        // Remove from budget when unmarked as purchased
        console.log('Removing from budget:', budgetItem.id)
        await firestoreService.deleteBudgetItemFirestore(eventId, budgetItem.id)
        showToast('Fjernet fra budget ✓')
      } else {
        console.log('Not adding to budget - purchased:', newPurchasedStatus, 'price:', item.price)
      }
    } catch (error) {
      console.error('Error toggling shopping purchased:', error)
      showToast('Fejl ved tilføjelse til budget')
    }
  }

  const addShoppingItem = async (eventId, itemData) => {
    try {
      const data = { ...itemData, purchased: false }
      Object.keys(data).forEach(key => data[key] === undefined && delete data[key])
      await firestoreService.addShoppingItemFirestore(eventId, data)
      showToast('Vare tilføjet! ✓')
    } catch (error) {
      console.error('Error adding shopping item:', error)
      throw error
    }
  }

  const addOrAggregateShoppingItem = async (eventId, itemData) => {
    try {
      const currentItems = shopping[eventId] || []
      const existingItem = currentItems.find(
        item => item.name.toLowerCase() === itemData.name.toLowerCase() &&
                 item.category === itemData.category
      )

      if (existingItem) {
        const newQuantity = (existingItem.quantity || 1) + (itemData.quantity || 1)
        await firestoreService.updateShoppingItemFirestore(eventId, existingItem.id, { quantity: newQuantity })
      } else {
        const data = { ...itemData, purchased: false }
        Object.keys(data).forEach(key => data[key] === undefined && delete data[key])
        await firestoreService.addShoppingItemFirestore(eventId, data)
      }
    } catch (error) {
      console.error('Error adding or aggregating shopping item:', error)
      throw error
    }
  }

  const toggleMealSelected = async (eventId, mealId) => {
    const meal = meals[eventId]?.find(m => m.id === mealId)
    console.log('toggleMealSelected - meal:', meal)
    if (!meal) return
    try {
      const newSelectedStatus = !meal.selected
      console.log('Updating meal selected status to:', newSelectedStatus)
      await firestoreService.updateMealFirestore(eventId, mealId, { selected: newSelectedStatus })

      // Transfer shopping items to shopping list when meal is selected
      if (newSelectedStatus && meal.shoppingList && meal.shoppingList.length > 0) {
        console.log('Transferring shopping items:', meal.shoppingList)
        for (const item of meal.shoppingList) {
          console.log('Processing item:', item)
          if (item.name) {
            const category = item.category || 'Kolonial'
            console.log('Adding to shopping list:', { name: item.name, category })
            await addOrAggregateShoppingItem(eventId, {
              name: item.name,
              category: category,
              quantity: item.quantity || 1,
              price: item.price,
              purchased: false,
              source: `meal:${mealId}`
            })
          }
        }
        showToast('Indkøbsliste opdateret! ✓')
      }
    } catch (error) {
      console.error('Error toggling meal selected:', error)
    }
  }

  const toggleMealShoppingItem = async (eventId, mealId, itemId) => {
    const meal = meals[eventId]?.find(m => m.id === mealId)
    if (!meal) return
    try {
      const newShoppingList = (meal.shoppingList || []).map(s => s.id === itemId ? { ...s, checked: !s.checked } : s)
      await firestoreService.updateMealFirestore(eventId, mealId, { shoppingList: newShoppingList })
    } catch (error) {
      console.error('Error toggling meal shopping item:', error)
    }
  }

  const updateMealAssignedTo = async (eventId, mealId, assignedTo) => {
    try {
      await firestoreService.updateMealFirestore(eventId, mealId, { assignedTo })
    } catch (error) {
      console.error('Error updating meal assigned to:', error)
    }
  }

  const updateMeal = async (eventId, mealId, updates) => {
    try {
      const meal = meals[eventId]?.find(m => m.id === mealId)

      // Safety check - make sure meal exists before updating
      if (!meal) {
        console.error('Meal not found:', { eventId, mealId })
        throw new Error(`Meal with ID ${mealId} not found in event ${eventId}`)
      }

      const oldShoppingList = meal?.shoppingList || []
      const newShoppingList = updates.shoppingList || []

      // Find items that were added (new items)
      const addedItems = newShoppingList.filter(newItem =>
        !oldShoppingList.some(oldItem => oldItem.id === newItem.id)
      )

      // Add new items to shopping list
      for (const item of addedItems) {
        if (item.name) {
          const category = item.category || 'Kolonial'
          await addOrAggregateShoppingItem(eventId, {
            name: item.name,
            category: category,
            quantity: item.quantity || 1,
            price: item.price,
            purchased: false
          })
        }
      }

      await firestoreService.updateMealFirestore(eventId, mealId, updates)
    } catch (error) {
      console.error('Error updating meal:', error)
      throw error
    }
  }

  const addMeal = async (eventId, meal) => {
    try {
      const mealData = {
        ...meal,
        selected: false,
        ingredients: meal.ingredients || [],
        steps: meal.steps || [],
        shoppingList: (meal.shoppingList || []).map(item => {
          const cleaned = { ...item }
          Object.keys(cleaned).forEach(key => cleaned[key] === undefined && delete cleaned[key])
          return cleaned
        }),
      }
      // Remove undefined values
      Object.keys(mealData).forEach(key => mealData[key] === undefined && delete mealData[key])
      await firestoreService.addMealFirestore(eventId, mealData)

      // Firestore subscription will update the state, no need for immediate update

      // Add shopping items to the shopping list immediately
      if (meal.shoppingList && meal.shoppingList.length > 0) {
        for (const item of meal.shoppingList) {
          if (item.name) {
            const category = item.category || 'Kolonial'
            const shoppingData = {
              name: item.name,
              category: category,
              quantity: item.quantity || 1,
              price: item.price,
              purchased: false
            }
            if (meal.category === 'Drink') {
              shoppingData.source = `drink:${meal.name}`
            }
            await addOrAggregateShoppingItem(eventId, shoppingData)
          }
        }
      }

      showToast('Ret tilføjet! ✓')
    } catch (error) {
      console.error('Error adding meal:', error)
      throw error
    }
  }

  const canDelete = (eventId) => {
    if (!events || !Array.isArray(events)) return false
    const event = events.find(e => e.id === eventId)

    // Check if user is owner via members array (mockData)
    const member = event?.members?.find(m => m.id === currentUser.id)
    if (member?.role === 'owner' || member?.role === 'co-owner') return true

    // Check if user created the event (Firebase)
    if (event?.createdBy === currentUser.id) return true

    return false
  }

  const deleteTask = async (eventId, taskId) => {
    if (!canDelete(eventId)) return
    try {
      await firestoreService.deleteTaskFirestore(eventId, taskId)
    } catch (error) {
      console.error('Error deleting task:', error)
      throw error
    }
  }

  const deleteGuest = async (eventId, guestId) => {
    if (!canDelete(eventId)) return
    try {
      await firestoreService.deleteGuestFirestore(eventId, guestId)
    } catch (error) {
      console.error('Error deleting guest:', error)
      throw error
    }
  }

  const deleteShoppingItem = async (eventId, itemId) => {
    if (!canDelete(eventId)) return
    try {
      // Get the shopping item to find matching budget item
      const item = shopping[eventId]?.find(i => i.id === itemId)

      // Delete from shopping list
      await firestoreService.deleteShoppingItemFirestore(eventId, itemId)

      // Also delete from budget if it exists there
      if (item && item.name) {
        const category = item.category || 'Mad'
        const budgetCategory = budget[eventId]?.find(cat => cat.name === category)
        const budgetItem = budgetCategory?.items?.find(bItem => bItem.name === item.name)

        if (budgetItem) {
          console.log('Deleting budget item:', budgetItem.id)
          await firestoreService.deleteBudgetItemFirestore(eventId, budgetItem.id)
          showToast('Slettet fra indkøbsliste og budget ✓')
        } else {
          showToast('Slettet fra indkøbsliste ✓')
        }
      }
    } catch (error) {
      console.error('Error deleting shopping item:', error)
      throw error
    }
  }

  const deleteMeal = async (eventId, mealId) => {
    if (!canDelete(eventId)) return
    try {
      const mealItem = meals[eventId]?.find(m => m.id === mealId)
      if (!mealItem) return

      // Find shopping items that were added from this meal
      let sourcePattern = null
      if (mealItem?.category === 'Drink') {
        sourcePattern = `drink:${mealItem.name}`
      } else {
        sourcePattern = `meal:${mealId}`
      }

      const shoppingItems = shopping[eventId]?.filter(s => s.source === sourcePattern)
      if (shoppingItems && shoppingItems.length > 0) {
        for (const item of shoppingItems) {
          const mealQuantity = item.quantity || 1

          // Reduce quantity instead of deleting completely
          const newQuantity = Math.max(0, (item.quantity || 1) - mealQuantity)

          if (newQuantity <= 0) {
            // Delete if quantity reaches 0
            await firestoreService.deleteShoppingItemFirestore(eventId, item.id)
          } else {
            // Update with reduced quantity
            await firestoreService.updateShoppingItemFirestore(eventId, item.id, { quantity: newQuantity })
          }
        }
      }

      await firestoreService.deleteMealFirestore(eventId, mealId)
      showToast('Ret slettet og indkøbsliste opdateret! ✓')
    } catch (error) {
      console.error('Error deleting meal:', error)
      throw error
    }
  }

  const addEvent = async (eventData) => {
    try {
      const eventId = await firestoreService.addEvent(eventData, firebaseUser.uid)
      showToast('Event oprettet! ✓')
      return { id: eventId, ...eventData }
    } catch (error) {
      console.error('Error adding event:', error)
      throw error
    }
  }

  const updateEvent = async (eventId, updates) => {
    try {
      await firestoreService.updateEventFirestore(eventId, updates)
    } catch (error) {
      console.error('Error updating event:', error)
      throw error
    }
  }

  const deleteEvent = async (eventId) => {
    if (!canDelete(eventId)) return
    try {
      await firestoreService.deleteEventFirestore(eventId)
      setEvents(prev => prev.filter(e => e.id !== eventId))
    } catch (error) {
      console.error('Error deleting event:', error)
      throw error
    }
  }

  const addMember = (eventId, member) => {
    setEvents(prev => prev.map(ev =>
      ev.id === eventId
        ? { ...ev, members: [...ev.members, { ...member, id: Date.now().toString() }] }
        : ev
    ))
  }

  const removeMember = async (eventId, memberId) => {
    try {
      const event = events.find(e => e.id === eventId)
      if (!event) return
      const updatedMembers = event.members.filter(m => m.id !== memberId)
      const updatedInvited = event.invited.filter(uid => uid !== memberId)
      await firestoreService.updateEventFirestore(eventId, {
        members: updatedMembers,
        invited: updatedInvited
      })
      setEvents(prev => prev.map(ev =>
        ev.id === eventId
          ? { ...ev, members: updatedMembers, invited: updatedInvited }
          : ev
      ))
    } catch (error) {
      console.error('Error removing member:', error)
      throw error
    }
  }

  const sendInvitation = async (eventId, toEmail, role, eventData) => {
    try {
      const event = eventData || events.find(e => e.id === eventId)
      if (!event) {
        throw new Error('Event not found')
      }
      await firestoreService.sendInvitationFirestore({
        eventId,
        toEmail,
        role,
        eventName: event.name,
        eventColor: event.color,
        fromUid: currentUser.id,
        fromName: currentUser.name,
        status: 'pending',
      })
      showToast('Invitation sendt! ✓')
    } catch (error) {
      console.error('Error sending invitation:', error)
      throw error
    }
  }

  const acceptInvitation = async (inv) => {
    try {
      // Fetch event directly from Firestore since it might not be in local events yet
      const event = await firestoreService.getEventFirestore(inv.eventId)
      if (!event) {
        console.error('Event not found for invitation')
        showToast('Fejl: Eventet blev ikke fundet')
        return
      }
      const newMember = { id: currentUser.id, name: currentUser.name, role: inv.role }
      const updatedMembers = [...(event.members || []), newMember]
      await firestoreService.updateEventFirestore(inv.eventId, {
        invited: arrayUnion(currentUser.id),
        members: updatedMembers,
      })
      // Try to update invitation status, but don't fail if it doesn't work
      try {
        await firestoreService.updateInvitationFirestore(inv.id, { status: 'accepted' })
      } catch (e) {
        console.warn('Could not update invitation status:', e)
      }
      // Mark as handled so it won't reappear in subscription
      setHandledInvitations(prev => new Set([...prev, inv.id]))
      showToast('Invitation accepteret! ✓')
    } catch (error) {
      console.error('Error accepting invitation:', error)
      showToast('Fejl ved acceptering af invitation')
      throw error
    }
  }

  const declineInvitation = async (invId) => {
    try {
      // Try to update in Firestore, but remove from local state regardless
      try {
        await firestoreService.updateInvitationFirestore(invId, { status: 'declined' })
      } catch (e) {
        console.warn('Could not update invitation status:', e)
      }
      // Mark as handled so it won't reappear in subscription
      setHandledInvitations(prev => new Set([...prev, invId]))
    } catch (error) {
      console.error('Error declining invitation:', error)
      throw error
    }
  }

  const addInventarItem = async (eventId, item) => {
    try {
      const itemData = { ...item }
      Object.keys(itemData).forEach(key => itemData[key] === undefined && delete itemData[key])
      await firestoreService.addInventarItemFirestore(eventId, itemData)
      showToast('Inventar tilføjet! ✓')
    } catch (error) {
      console.error('Error adding inventar item:', error)
      throw error
    }
  }

  const updateInventarItem = async (eventId, itemId, updates) => {
    try {
      Object.keys(updates).forEach(key => updates[key] === undefined && delete updates[key])
      await firestoreService.updateInventarItemFirestore(eventId, itemId, updates)
    } catch (error) {
      console.error('Error updating inventar item:', error)
      throw error
    }
  }

  const deleteInventarItem = async (eventId, itemId) => {
    try {
      await firestoreService.deleteInventarItemFirestore(eventId, itemId)
    } catch (error) {
      console.error('Error deleting inventar item:', error)
      throw error
    }
  }

  const addCatering = async (eventId, data) => {
    try {
      const cateringData = { ...data }
      Object.keys(cateringData).forEach(key => cateringData[key] === undefined && delete cateringData[key])
      await firestoreService.addCateringFirestore(eventId, cateringData)
      if (cateringData.pris > 0) {
        await firestoreService.addShoppingItemFirestore(eventId, {
          name: `Catering: ${cateringData.firma}`,
          category: 'Mad ud af huset',
          price: cateringData.pris,
          quantity: 1,
          checked: false
        })
      }
      showToast('Catering tilføjet! ✓')
    } catch (error) {
      console.error('Error adding catering:', error)
      throw error
    }
  }

  const updateCatering = async (eventId, id, updates) => {
    try {
      Object.keys(updates).forEach(key => updates[key] === undefined && delete updates[key])
      await firestoreService.updateCateringFirestore(eventId, id, updates)
    } catch (error) {
      console.error('Error updating catering:', error)
      throw error
    }
  }

  const deleteCatering = async (eventId, id) => {
    try {
      const cateringItem = catering[eventId]?.find(c => c.id === id)
      await firestoreService.deleteCateringFirestore(eventId, id)
      // Delete corresponding shopping item
      if (cateringItem) {
        const shoppingItems = shopping[eventId] || []
        const shoppingItem = shoppingItems.find(s => s.name === `Catering: ${cateringItem.firma}`)
        if (shoppingItem) {
          await firestoreService.deleteShoppingItemFirestore(eventId, shoppingItem.id)
        }
      }
    } catch (error) {
      console.error('Error deleting catering:', error)
      throw error
    }
  }

  const addRestaurant = async (eventId, data) => {
    try {
      const restaurantData = { ...data }
      Object.keys(restaurantData).forEach(key => restaurantData[key] === undefined && delete restaurantData[key])
      await firestoreService.addRestaurantFirestore(eventId, restaurantData)
      if (restaurantData.pris > 0) {
        await firestoreService.addShoppingItemFirestore(eventId, {
          name: `Restaurant: ${restaurantData.navn}`,
          category: 'Mad ud af huset',
          price: restaurantData.pris,
          quantity: 1,
          checked: false
        })
      }
      showToast('Restaurant tilføjet! ✓')
    } catch (error) {
      console.error('Error adding restaurant:', error)
      throw error
    }
  }

  const updateRestaurant = async (eventId, id, updates) => {
    try {
      Object.keys(updates).forEach(key => updates[key] === undefined && delete updates[key])
      await firestoreService.updateRestaurantFirestore(eventId, id, updates)
    } catch (error) {
      console.error('Error updating restaurant:', error)
      throw error
    }
  }

  const deleteRestaurant = async (eventId, id) => {
    try {
      const restaurantItem = restaurants[eventId]?.find(r => r.id === id)
      await firestoreService.deleteRestaurantFirestore(eventId, id)
      // Delete corresponding shopping item
      if (restaurantItem) {
        const shoppingItems = shopping[eventId] || []
        const shoppingItem = shoppingItems.find(s => s.name === `Restaurant: ${restaurantItem.navn}`)
        if (shoppingItem) {
          await firestoreService.deleteShoppingItemFirestore(eventId, shoppingItem.id)
        }
      }
    } catch (error) {
      console.error('Error deleting restaurant:', error)
      throw error
    }
  }

  const addMusik = async (eventId, data) => {
    try {
      const musikData = { ...data }
      Object.keys(musikData).forEach(key => musikData[key] === undefined && delete musikData[key])
      await firestoreService.addMusikFirestore(eventId, musikData)
      if (musikData.pris > 0) {
        await firestoreService.addShoppingItemFirestore(eventId, {
          name: `Musik: ${musikData.navn}`,
          category: 'Musik & Underholdning',
          price: musikData.pris,
          quantity: 1,
          purchased: false,
        })
      }
      showToast('Musikbooking tilføjet! ✓')
    } catch (error) {
      console.error('Error adding musik:', error)
      throw error
    }
  }

  const updateMusik = async (eventId, id, updates) => {
    try {
      Object.keys(updates).forEach(key => updates[key] === undefined && delete updates[key])
      if (updates.navn !== undefined) {
        const oldItem = musik[eventId]?.find(m => m.id === id)
        if (oldItem?.pris > 0) {
          const shoppingItem = shopping[eventId]?.find(s => s.name === `Musik: ${oldItem.navn}`)
          if (shoppingItem) {
            await firestoreService.updateShoppingItemFirestore(eventId, shoppingItem.id, {
              name: `Musik: ${updates.navn}`,
            })
          }
        }
      }
      await firestoreService.updateMusikFirestore(eventId, id, updates)
    } catch (error) {
      console.error('Error updating musik:', error)
      throw error
    }
  }

  const deleteMusik = async (eventId, id) => {
    try {
      const musikItem = musik[eventId]?.find(m => m.id === id)
      if (musikItem?.pris > 0) {
        const shoppingItem = shopping[eventId]?.find(s => s.name === `Musik: ${musikItem.navn}`)
        if (shoppingItem) {
          await firestoreService.deleteShoppingItemFirestore(eventId, shoppingItem.id)
        }
      }
      await firestoreService.deleteMusikFirestore(eventId, id)
    } catch (error) {
      console.error('Error deleting musik:', error)
      throw error
    }
  }

  const addTransport = async (eventId, data) => {
    try {
      const transportData = { ...data }
      Object.keys(transportData).forEach(key => transportData[key] === undefined && delete transportData[key])
      await firestoreService.addTransportFirestore(eventId, transportData)
      if (transportData.pris > 0) {
        await firestoreService.addShoppingItemFirestore(eventId, {
          name: `Transport: ${transportData.navn}`,
          category: 'Transport',
          price: transportData.pris,
          quantity: 1,
          purchased: false,
        })
      }
      showToast('Transport tilføjet! ✓')
    } catch (error) {
      console.error('Error adding transport:', error)
      throw error
    }
  }

  const updateTransport = async (eventId, id, updates) => {
    try {
      Object.keys(updates).forEach(key => updates[key] === undefined && delete updates[key])
      if (updates.navn !== undefined) {
        const oldItem = transport[eventId]?.find(t => t.id === id)
        if (oldItem?.pris > 0) {
          const shoppingItem = shopping[eventId]?.find(s => s.name === `Transport: ${oldItem.navn}`)
          if (shoppingItem) {
            await firestoreService.updateShoppingItemFirestore(eventId, shoppingItem.id, {
              name: `Transport: ${updates.navn}`,
            })
          }
        }
      }
      await firestoreService.updateTransportFirestore(eventId, id, updates)
    } catch (error) {
      console.error('Error updating transport:', error)
      throw error
    }
  }

  const deleteTransport = async (eventId, id) => {
    try {
      const transportItem = transport[eventId]?.find(t => t.id === id)
      if (transportItem?.pris > 0) {
        const shoppingItem = shopping[eventId]?.find(s => s.name === `Transport: ${transportItem.navn}`)
        if (shoppingItem) {
          await firestoreService.deleteShoppingItemFirestore(eventId, shoppingItem.id)
        }
      }
      await firestoreService.deleteTransportFirestore(eventId, id)
    } catch (error) {
      console.error('Error deleting transport:', error)
      throw error
    }
  }

  const addVenue = async (eventId, data) => {
    try {
      const venueData = { ...data }
      Object.keys(venueData).forEach(key => venueData[key] === undefined && delete venueData[key])
      await firestoreService.addVenueFirestore(eventId, venueData)
      showToast('Venue tilføjet! ✓')
    } catch (error) {
      console.error('Error adding venue:', error)
      throw error
    }
  }

  const updateVenue = async (eventId, id, updates) => {
    try {
      Object.keys(updates).forEach(key => updates[key] === undefined && delete updates[key])
      await firestoreService.updateVenueFirestore(eventId, id, updates)
      showToast('Venue opdateret! ✓')
    } catch (error) {
      console.error('Error updating venue:', error)
      throw error
    }
  }

  const deleteVenue = async (eventId, id) => {
    try {
      await firestoreService.deleteVenueFirestore(eventId, id)
      showToast('Venue slettet! ✓')
    } catch (error) {
      console.error('Error deleting venue:', error)
      throw error
    }
  }

  const cleanupOrphanShoppingItems = async (eventId) => {
    try {
      const cateringItems = catering[eventId] || []
      const restaurantItems = restaurants[eventId] || []
      const shoppingItems = shopping[eventId] || []

      let deleted = 0
      for (const item of shoppingItems) {
        const isCateringItem = item.name.startsWith('Catering:') && cateringItems.some(c => `Catering: ${c.firma}` === item.name)
        const isRestaurantItem = item.name.startsWith('Restaurant:') && restaurantItems.some(r => `Restaurant: ${r.navn}` === item.name)

        if ((item.name.startsWith('Catering:') || item.name.startsWith('Restaurant:')) && !isCateringItem && !isRestaurantItem) {
          await firestoreService.deleteShoppingItemFirestore(eventId, item.id)
          deleted++
        }
      }

      if (deleted > 0) {
        showToast(`${deleted} forældet item(s) slettet! ✓`)
      } else {
        showToast('Ingen forældet items fundet ✓')
      }
    } catch (error) {
      console.error('Error cleaning up:', error)
      showToast('Fejl ved oprydning')
    }
  }

  const cleanupDuplicateMeals = async (eventId) => {
    try {
      const eventMeals = meals[eventId] || []
      const seenIds = new Set()
      let deleted = 0

      for (const meal of eventMeals) {
        if (seenIds.has(meal.id)) {
          console.log('Deleting duplicate meal:', meal.name, meal.id)
          await firestoreService.deleteMealFirestore(eventId, meal.id)
          deleted++
        } else {
          seenIds.add(meal.id)
        }
      }

      if (deleted > 0) {
        showToast(`${deleted} duplikat ret(ter) slettet! ✓`)
      } else {
        showToast('Ingen duplikater fundet ✓')
      }
    } catch (error) {
      console.error('Error cleaning up duplicates:', error)
      showToast('Fejl ved sletning af duplikater')
    }
  }

  const addFile = async (eventId, fileData) => {
    try {
      await firestoreService.addFileFirestore(eventId, fileData)
      showToast('Fil uploadet! ✓')
    } catch (error) {
      console.error('Error adding file:', error)
      throw error
    }
  }

  const updateFile = async (eventId, fileId, updates) => {
    try {
      await firestoreService.updateFileFirestore(eventId, fileId, updates)
    } catch (error) {
      console.error('Error updating file:', error)
      throw error
    }
  }

  const deleteFile = async (eventId, fileId) => {
    try {
      await firestoreService.deleteFileFirestore(eventId, fileId)
      showToast('Fil slettet! ✓')
    } catch (error) {
      console.error('Error deleting file:', error)
      throw error
    }
  }

  return (
    <AppContext.Provider value={{
      currentUser,
      events,
      addEvent,
      updateEvent,
      deleteEvent,
      tasks, toggleTask, addTask, updateTask, addComment, deleteTask,
      guests, updateGuestStatus, addGuest, deleteGuest,
      budget, addBudgetItem,
      shopping, updateShoppingQty, toggleShoppingPurchased, addShoppingItem, addOrAggregateShoppingItem, deleteShoppingItem,
      meals, toggleMealSelected, toggleMealShoppingItem, updateMealAssignedTo, updateMeal, addMeal, deleteMeal,
      inventar, addInventarItem, updateInventarItem, deleteInventarItem,
      catering, addCatering, updateCatering, deleteCatering,
      restaurants, addRestaurant, updateRestaurant, deleteRestaurant,
      musik, addMusik, updateMusik, deleteMusik,
      transport, addTransport, updateTransport, deleteTransport,
      venues, addVenue, updateVenue, deleteVenue,
      files, addFile, updateFile, deleteFile,
      cleanupOrphanShoppingItems,
      cleanupDuplicateMeals,
      canDelete, addMember, removeMember,
      invitations, sendInvitation, acceptInvitation, declineInvitation,
      toastMessage, showToast,
    }}>
      {children}
    </AppContext.Provider>
  )
}

export function useApp() {
  return useContext(AppContext)
}
